// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// render.ts — the common, as DOM. Cells, hedges and runners are absolutely positioned from
// layout.ts's arithmetic, so every position in here is a number a unit test can check rather than
// something only a browser knows (principle #20 — jsdom has no layout engine, so anything the
// stylesheet decides is invisible to the suite).
//
// Every colour comes from palette.ts. Nothing here may hard-code one: a literal in a draw call is
// a colour nobody can measure, which is exactly how Scrapwall shipped invisible walls.

import { computeLayout, cellXY, hedgeRect, type Layout } from './layout';
import { PALETTE, colourOf, glyphOf } from './palette';
import { orientOf, FLAT, type Cell, type Hedge } from './rules';
import type { Game } from './game';

export interface View {
  /** Re-measure and re-place everything. Returns false if the box is not laid out yet. */
  resize(): boolean;
  /** Repaint from the game state. */
  paint(g: Game, opts: PaintOpts): void;
  layout(): Layout;
  /** Convert a client point into board-local px. */
  toBoard(clientX: number, clientY: number): { x: number; y: number };
  destroy(): void;
}

export interface PaintOpts {
  /** Steps the local player may take right now (empty when it is not their turn). */
  steps: readonly Cell[];
  /** The hedge currently being aimed, if any. */
  ghost: Hedge | null;
  /** True while the player is choosing a hedge rather than a step. */
  planting: boolean;
  /** Seat of the local player, or -1 when spectating. */
  self: number;
  /** A step aimed at but not yet confirmed, drawn larger so the choice is unmistakable. */
  aimed?: Cell | null;
}

export function createView(host: HTMLElement, size: number): View {
  host.classList.add('board');
  host.innerHTML = '';

  const turf = document.createElement('div');
  turf.className = 'turf-layer';
  const hedges = document.createElement('div');
  hedges.className = 'hedge-layer';
  const marks = document.createElement('div');
  marks.className = 'mark-layer';
  const pawns = document.createElement('div');
  pawns.className = 'pawn-layer';
  host.append(turf, hedges, marks, pawns);

  let layout = computeLayout(size, 320, 320);
  const cells: HTMLElement[] = [];
  for (let i = 0; i < size * size; i++) {
    const el = document.createElement('div');
    el.className = 'cell';
    // A checker so a step is legible as a step. Both tones are pinned by the contrast test.
    el.style.background = ((((i / size) | 0) + (i % size)) & 1) === 0 ? PALETTE.board : PALETTE.turf;
    turf.appendChild(el);
    cells.push(el);
  }

  const place = (): void => {
    host.style.width = `${layout.board}px`;
    host.style.height = `${layout.board}px`;
    for (let i = 0; i < cells.length; i++) {
      const { x, y } = cellXY(layout, i);
      const el = cells[i];
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.width = `${layout.cell}px`;
      el.style.height = `${layout.cell}px`;
    }
  };
  place();

  function resize(): boolean {
    // Measure the PARENT: the host's own size is what this calculation sets, so measuring it would
    // feed the result back in and shrink the board a little on every call.
    const parent = host.parentElement;
    const w = parent ? parent.clientWidth : 0;
    const h = parent ? parent.clientHeight : 0;
    if (w < 2 || h < 2) return false; // hidden tab or not laid out yet — never compute from 0
    const next = computeLayout(size, w, h);
    if (next.cell === layout.cell && next.gap === layout.gap) return true;
    layout = next;
    place();
    return true;
  }

  function paint(g: Game, opts: PaintOpts): void {
    // --- goal edges, so "which way am I running" is never a guess
    marks.innerHTML = '';
    for (let seat = 0; seat < g.state.runners.length; seat++) {
      const r = g.state.runners[seat];
      const bar = document.createElement('div');
      bar.className = 'goal-bar';
      bar.style.background = colourOf(seat);
      const thick = Math.max(3, Math.round(layout.gap * 0.8));
      if (r.goal === 0) Object.assign(bar.style, { left: '0px', top: `${-thick - 2}px`, width: `${layout.board}px`, height: `${thick}px` });
      else if (r.goal === 1) Object.assign(bar.style, { left: '0px', top: `${layout.board + 2}px`, width: `${layout.board}px`, height: `${thick}px` });
      else if (r.goal === 2) Object.assign(bar.style, { left: `${-thick - 2}px`, top: '0px', width: `${thick}px`, height: `${layout.board}px` });
      else Object.assign(bar.style, { left: `${layout.board + 2}px`, top: '0px', width: `${thick}px`, height: `${layout.board}px` });
      marks.appendChild(bar);
    }

    // --- legal steps, drawn as a target the size of the cell but hit-tested by nearest-wins
    for (const cell of opts.steps) {
      const { x, y } = cellXY(layout, cell);
      const dot = document.createElement('div');
      const isAimed = opts.aimed === cell;
      dot.className = `step-target${isAimed ? ' aimed' : ''}`;
      const d = Math.round(layout.cell * (isAimed ? 0.78 : 0.42));
      Object.assign(dot.style, {
        left: `${x + (layout.cell - d) / 2}px`,
        top: `${y + (layout.cell - d) / 2}px`,
        width: `${d}px`,
        height: `${d}px`,
        borderColor: PALETTE.target,
      });
      marks.appendChild(dot);
    }

    // --- hedges
    hedges.innerHTML = '';
    const draw = (h: Hedge, cls: string, colour: string): void => {
      const r = hedgeRect(layout, h);
      const el = document.createElement('div');
      el.className = cls;
      Object.assign(el.style, {
        left: `${r.x}px`,
        top: `${r.y}px`,
        width: `${r.w}px`,
        height: `${r.h}px`,
        background: colour,
      });
      el.dataset.orient = orientOf(h) === FLAT ? 'flat' : 'upright';
      hedges.appendChild(el);
    };
    for (const h of g.state.placed) draw(h, 'hedge', PALETTE.hedge);
    if (opts.ghost !== null && opts.planting) draw(opts.ghost, 'hedge ghost', PALETTE.ghost);

    // --- runners
    pawns.innerHTML = '';
    for (let seat = 0; seat < g.state.runners.length; seat++) {
      const r = g.state.runners[seat];
      const { x, y } = cellXY(layout, r.at);
      const el = document.createElement('div');
      el.className = `pawn${seat === opts.self ? ' self' : ''}${seat === g.turn && !g.over ? ' to-move' : ''}`;
      const d = Math.round(layout.cell * 0.74);
      Object.assign(el.style, {
        left: `${x + (layout.cell - d) / 2}px`,
        top: `${y + (layout.cell - d) / 2}px`,
        width: `${d}px`,
        height: `${d}px`,
        background: colourOf(seat),
        color: PALETTE.ink,
        fontSize: `${Math.round(d * 0.52)}px`,
      });
      el.textContent = glyphOf(seat);
      el.setAttribute('aria-label', `Runner ${seat + 1}`);
      pawns.appendChild(el);
    }
  }

  function toBoard(clientX: number, clientY: number): { x: number; y: number } {
    // clientX/Y minus the rect — never offsetX/offsetY, which scale oddly under DPR and zoom and
    // are relative to whichever child happened to be under the finger.
    const rect = host.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  return {
    resize,
    paint,
    layout: () => layout,
    toBoard,
    destroy: () => {
      host.innerHTML = '';
      host.classList.remove('board');
    },
  };
}
